import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Button } from '../../components/ui/Button';
import { Card, Chip } from '../../components/ui/Card';
import { SkeletonRow } from '../../components/ui/Loader';
import { api } from '../../lib/api';
import { useDocumentTitle } from '../../lib/title';
import { Body } from '../announcements/WhatsNewPage';

interface Entry {
  id: number;
  title: string;
  body: string;
  version: string | null;
  published_at: string | null;
  created_at: string;
  author: string | null;
}

/**
 * Writing what changed.
 *
 * ## Saving and publishing are separate, deliberately
 *
 * A new note is a draft. The alternative is a text box that broadcasts the
 * moment somebody presses save - which is how a half-finished release note goes
 * out, and there is no unsending it: the people who read it have read it.
 *
 * Withdrawing is the closest thing to an undo and is honest about its limits.
 * It takes the note off the page; it does not take it out of anybody's memory.
 *
 * ## The preview is the same component the readers get
 *
 * `Body` is imported from the page rather than reimplemented, so what an
 * author sees while writing is what a reader sees afterwards. Two renderers
 * would drift, and the first sign of it would be a published note that looks
 * wrong to everybody except the person who wrote it.
 */
export function AdminAnnouncementsPage() {
  useDocumentTitle('Announcements');

  const queryClient = useQueryClient();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [version, setVersion] = useState('');

  const { data, isPending } = useQuery({
    queryKey: ['admin', 'announcements'],
    queryFn: () => api.get<{ items: Entry[] }>('/admin/announcements'),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'announcements'] });
    // The reader-facing list and the unread dot both read this key.
    void queryClient.invalidateQueries({ queryKey: ['announcements'] });
  };

  const create = useMutation({
    mutationFn: () =>
      api.post('/admin/announcements', {
        title: title.trim(),
        body: body.trim(),
        version: version.trim(),
      }),
    onSuccess: () => {
      setTitle('');
      setBody('');
      setVersion('');
      refresh();
    },
  });

  const change = useMutation({
    mutationFn: ({ id, patch }: { id: number; patch: Record<string, unknown> }) =>
      api.patch(`/admin/announcements/${id}`, patch),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/admin/announcements/${id}`),
    onSuccess: refresh,
  });

  const items = data?.items ?? [];
  const ready = title.trim() !== '' && body.trim() !== '';

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold tracking-tight">Announcements</h1>
        <p className="max-w-2xl text-sm leading-relaxed text-soft">
          What users read at{' '}
          <Link to="/whats-new" className="text-accent hover:underline">
            /whats-new
          </Link>
          . Nothing is visible until you publish it.
        </p>
      </header>

      <Card className="flex flex-col gap-3 p-5">
        <h2 className="meta-caps text-faint">Write one</h2>

        <input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          maxLength={140}
          placeholder="What changed"
          className="rounded-[var(--radius-control)] border border-line bg-raised px-3 py-2.5 text-sm text-ink outline-none placeholder:text-faint focus:border-line-lit"
        />

        <textarea
          value={body}
          onChange={(event) => setBody(event.target.value)}
          maxLength={8000}
          rows={7}
          placeholder={'A paragraph explaining it.\n\n- a bullet\n- another bullet'}
          className="rounded-[var(--radius-control)] border border-line bg-raised px-3 py-2.5 text-sm leading-relaxed text-ink outline-none placeholder:text-faint focus:border-line-lit"
        />

        <div className="flex flex-wrap items-center gap-3">
          <input
            value={version}
            onChange={(event) => setVersion(event.target.value)}
            maxLength={32}
            placeholder="Version (optional)"
            className="w-44 rounded-[var(--radius-control)] border border-line bg-raised px-3 py-2 font-mono text-[13px] text-ink outline-none placeholder:text-faint focus:border-line-lit"
          />

          <Button disabled={!ready || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? 'Saving…' : 'Save as draft'}
          </Button>

          {/* Said out loud, because "save" meaning "publish" is the mistake
              this screen is shaped to prevent. */}
          <span className="meta text-faint">Nobody sees it until you publish.</span>
        </div>

        {body.trim() !== '' && (
          <div className="flex flex-col gap-2 border-t border-line pt-4">
            <span className="meta-caps text-faint">Preview</span>
            <Body text={body} />
          </div>
        )}
      </Card>

      {isPending ? (
        <div className="flex flex-col gap-2" aria-busy="true">
          {[0, 1].map((i) => (
            <SkeletonRow key={i} />
          ))}
        </div>
      ) : (
        <ul className="flex flex-col gap-3" data-admin-announcements>
          {items.map((entry) => (
            <li key={entry.id}>
              <Card className="flex flex-col gap-3 p-5">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <h3 className="text-base font-semibold text-ink">{entry.title}</h3>

                  {entry.published_at === null ? (
                    <Chip>Draft</Chip>
                  ) : (
                    <span className="meta-caps text-faint">
                      {new Date(entry.published_at).toLocaleDateString()}
                      {entry.version !== null && ` · ${entry.version}`}
                    </span>
                  )}
                </div>

                <Body text={entry.body} />

                <div className="flex flex-wrap items-center gap-2 border-t border-line pt-3">
                  {entry.published_at === null ? (
                    <Button
                      disabled={change.isPending}
                      data-publish={entry.id}
                      onClick={() => change.mutate({ id: entry.id, patch: { published: true } })}
                    >
                      Publish
                    </Button>
                  ) : (
                    <Button
                      variant="secondary"
                      disabled={change.isPending}
                      data-withdraw={entry.id}
                      onClick={() => change.mutate({ id: entry.id, patch: { published: false } })}
                    >
                      Withdraw
                    </Button>
                  )}

                  <button
                    type="button"
                    data-delete={entry.id}
                    disabled={remove.isPending}
                    onClick={() => {
                      /*
                       * Confirmed, and the confirmation says what it costs. A
                       * published note is something people have read and linked
                       * to; deleting it is not the same as withdrawing it.
                       */
                      const asked =
                        entry.published_at === null
                          ? 'Delete this draft?'
                          : 'Delete this permanently? Withdrawing takes it off the page without losing it.';

                      if (window.confirm(asked)) remove.mutate(entry.id);
                    }}
                    className="meta-caps text-faint transition-colors hover:text-hot-text"
                  >
                    Delete
                  </button>

                  {entry.author !== null && (
                    <span className="meta ml-auto text-ghost">{entry.author}</span>
                  )}
                </div>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
